// src/components/MatchPrompt.js
//
// Chunk 5 — the shared "Apply to this bill/debt?" confirmation, used by every
// transaction path (QuickAdd, Receipt Scanner, Log manual-apply, Bulk Import
// review). CONTENT-ONLY: it renders no overlay of its own, so callers place it
// where it fits — QuickAdd/Scanner drop it inside their existing <Sheet> (a step,
// not a modal-over-modal); Log/Bulk wrap it in a lightweight overlay.
//
// It operates on a LIST of transactions ({ id, amount, transaction_date }) so a
// Bulk Import review can resolve every same-merchant row in one answer. The
// single-transaction callers just pass a one-element list.
//
// Two entry modes:
//   • target provided  → the confirm view (Apply / Different / Just log)
//   • target null      → jump straight to the picker (manual "Apply to bill/debt")
//
import React, { useState } from 'react'
import { theme } from '../lib/theme'
import { useCurrency } from '../lib/CurrencyContext'
import {
  normalizeMerchant,
  applyTransactionToBill,
  applyTransactionToDebt,
  saveMerchantRule,
} from '../lib/merchantMatching'

const c = theme.colors

export default function MatchPrompt({
  merchantName,
  transactions = [],
  kind = null,          // 'bill' | 'debt' | null (null → picker-first, manual apply)
  target = null,        // matched bill/debt object, or null for manual apply
  bills = [],
  debts = [],
  userId,
  onDone,               // (result) => void — after any resolution
  onCancel,             // optional back-out (leaves txs unmatched, no rule saved)
}) {
  const { formatCurrency } = useCurrency()
  const [mode, setMode] = useState(target ? 'confirm' : 'picker')
  const [busy, setBusy] = useState(false)
  const norm = normalizeMerchant(merchantName)

  const applyToTarget = async (t, k) => {
    if (busy) return
    setBusy(true)
    for (const tx of transactions) {
      if (k === 'bill') {
        await applyTransactionToBill(tx.id, t.id)
      } else {
        const paidAt = tx.transaction_date
          ? new Date(tx.transaction_date + 'T12:00:00').toISOString()
          : null
        await applyTransactionToDebt(tx.id, t.id, tx.amount, paidAt)
      }
    }
    // Remember the decision so the same merchant auto-applies next time.
    if (norm) {
      await saveMerchantRule(userId, norm, k === 'bill' ? 'apply_to_bill' : 'apply_to_debt', t.id, k)
    }
    setBusy(false)
    onDone && onDone({ action: 'applied', kind: k, target: t })
  }

  const justLog = async () => {
    if (busy) return
    setBusy(true)
    // never_match is per-pair — only meaningful when we have a detected target.
    if (norm && target && kind) {
      await saveMerchantRule(userId, norm, 'never_match', target.id, kind)
    }
    setBusy(false)
    onDone && onDone({ action: 'skipped' })
  }

  // Header line: "Merchant · $X · Jun 3" for one, "Merchant · N transactions" for many.
  const headerMeta = (() => {
    if (transactions.length === 1) {
      const tx = transactions[0]
      const amt = tx.amount != null ? formatCurrency(tx.amount) : null
      const dt = tx.transaction_date
        ? new Date(tx.transaction_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : null
      return [amt, dt].filter(Boolean).join(' · ')
    }
    return `${transactions.length} transactions`
  })()

  // ─── Picker view (Different bill/debt, or manual apply) ──────
  if (mode === 'picker') {
    return (
      <div style={styles.card}>
        <div style={styles.title}>
          {target ? 'Pick a different bill or debt' : 'Apply to a bill or debt'}
        </div>
        <div style={styles.metaLine}>
          {merchantName || 'This transaction'}{headerMeta ? ` · ${headerMeta}` : ''}
        </div>
        <div style={styles.pickerScroll}>
          {bills.length > 0 && <div style={styles.pickerGroupLabel}>Bills</div>}
          {bills.map(b => (
            <button
              key={`bill-${b.id}`}
              type="button"
              className="btn-compact"
              style={styles.pickerRow}
              disabled={busy}
              onClick={() => applyToTarget(b, 'bill')}
            >
              <span style={styles.pickerName}>{b.name}</span>
              <span style={styles.pickerAmt}>{b.amount != null ? formatCurrency(b.amount) : ''}</span>
            </button>
          ))}
          {debts.length > 0 && <div style={styles.pickerGroupLabel}>Debts</div>}
          {debts.map(d => (
            <button
              key={`debt-${d.id}`}
              type="button"
              className="btn-compact"
              style={styles.pickerRow}
              disabled={busy}
              onClick={() => applyToTarget(d, 'debt')}
            >
              <span style={styles.pickerName}>{d.name}</span>
              <span style={styles.pickerAmt}>{d.current_balance != null ? formatCurrency(d.current_balance) : ''}</span>
            </button>
          ))}
          {bills.length === 0 && debts.length === 0 && (
            <div style={styles.emptyPicker}>No bills or debts to apply to.</div>
          )}
        </div>
        <div style={styles.actions}>
          <button
            type="button"
            className="btn-compact"
            style={styles.secondaryBtn}
            disabled={busy}
            onClick={() => (target ? setMode('confirm') : (onCancel && onCancel()))}
          >
            {target ? 'Back' : 'Cancel'}
          </button>
        </div>
      </div>
    )
  }

  // ─── Confirm view ────────────────────────────────────────────
  const targetNoun = kind === 'debt' ? 'debt' : 'bill'
  return (
    <div style={styles.card}>
      <div style={styles.title}>
        {kind === 'debt' ? 'Debt payment detected' : 'Bill payment detected'}
      </div>
      <div style={styles.metaLine}>
        {merchantName || 'This transaction'}{headerMeta ? ` · ${headerMeta}` : ''}
      </div>
      <div style={styles.body}>
        This looks like your{' '}
        <strong style={{ color: c.plum, fontWeight: 600 }}>{target?.name}</strong> {targetNoun}.
        {transactions.length > 1 ? ` Apply all ${transactions.length}?` : ' Apply it?'}
      </div>
      <div style={styles.actionsStack}>
        <button
          type="button"
          style={styles.primaryBtn}
          disabled={busy}
          onClick={() => applyToTarget(target, kind)}
        >
          {busy ? 'Applying…' : `Apply to ${target?.name}`}
        </button>
        <button
          type="button"
          className="btn-compact"
          style={styles.secondaryBtn}
          disabled={busy}
          onClick={() => setMode('picker')}
        >
          Different bill or debt
        </button>
        <button
          type="button"
          className="btn-compact"
          style={styles.tertiaryBtn}
          disabled={busy}
          onClick={justLog}
        >
          Just log {transactions.length > 1 ? 'them' : 'this'}
        </button>
      </div>
    </div>
  )
}

// Convenience overlay wrapper for callers that aren't already inside a modal
// (Log, Bulk Import). QuickAdd/Scanner use <MatchPrompt> bare inside their Sheet.
export function MatchPromptOverlay(props) {
  return (
    <div style={styles.overlay} role="alertdialog" aria-modal="true">
      <div style={styles.overlayInner}>
        <MatchPrompt {...props} />
      </div>
    </div>
  )
}

const styles = {
  card: {
    display: 'flex', flexDirection: 'column', gap: '10px',
    padding: '20px', maxWidth: '380px', width: '100%',
  },
  title: {
    fontFamily: theme.fonts.display, fontWeight: 600,
    fontSize: '18px', letterSpacing: '-0.015em', color: c.plum,
  },
  metaLine: { fontSize: '13px', color: c.inkMuted },
  body: { fontSize: '14px', color: c.plumSoft, lineHeight: 1.5, marginTop: '2px' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '4px' },
  actionsStack: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' },
  primaryBtn: {
    background: c.green, color: '#FFFFFF', border: 'none',
    padding: '13px 16px', borderRadius: '12px',
    fontFamily: theme.fonts.body, fontSize: '15px', fontWeight: 600, cursor: 'pointer',
  },
  secondaryBtn: {
    background: c.cream2, color: c.plum, border: `1px solid ${c.lineStrong}`,
    padding: '11px 16px', borderRadius: '12px',
    fontFamily: theme.fonts.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },
  tertiaryBtn: {
    background: 'transparent', color: c.inkMuted, border: 'none',
    padding: '9px 16px', borderRadius: '12px',
    fontFamily: theme.fonts.body, fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
  },
  pickerScroll: {
    display: 'flex', flexDirection: 'column', gap: '2px',
    maxHeight: '260px', overflowY: 'auto', margin: '4px 0',
  },
  pickerGroupLabel: {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: c.inkQuiet, padding: '8px 4px 4px',
  },
  pickerRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '12px', padding: '11px 12px', borderRadius: '10px',
    background: 'transparent', border: 'none', cursor: 'pointer', width: '100%',
    textAlign: 'left',
  },
  pickerName: { fontSize: '14px', fontWeight: 500, color: c.plum },
  pickerAmt: { fontSize: '13px', color: c.inkMuted, flexShrink: 0 },
  emptyPicker: { fontSize: '13px', color: c.inkMuted, padding: '16px 4px' },
  overlay: {
    position: 'fixed', inset: 0, overscrollBehaviorX: 'contain', background: 'rgba(30, 16, 64, 0.34)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', zIndex: 1000,
  },
  overlayInner: {
    overscrollBehaviorX: 'contain',
    background: c.white, borderRadius: '18px', width: '100%', maxWidth: '380px',
    boxShadow: '0 24px 60px rgba(30,16,64,0.28)',
  },
}
